<template>
  <div class="multi-window-container">
    <!-- 顶部工具栏 -->
    <div class="multi-window-toolbar">
      <div class="toolbar-left">
        <el-radio-group v-model="layoutMode" size="small" style="margin-right: 8px">
          <el-radio-button value="grid">
            <el-icon><Grid /></el-icon> {{ t('message.multiWindow.grid') || '网格' }}
          </el-radio-button>
          <el-radio-button value="mdi">
            <el-icon><CopyDocument /></el-icon> {{ t('message.multiWindow.multiForm') || '多窗体' }}
          </el-radio-button>
        </el-radio-group>

        <el-dropdown size="small" @command="handleAddWindow">
          <el-button type="primary" size="small" plain>
            <el-icon><Plus /></el-icon>
            {{ t('message.multiWindow.addWindow') || '添加窗体' }}
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="win in availableWindowOptions"
                :key="win.key"
                :command="win.key"
                :disabled="win.disabled"
              >
                {{ win.label }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <el-button size="small" type="danger" plain @click="clearAllWindows">
          <el-icon><Delete /></el-icon>
          {{ t('message.multiWindow.clearWindows') || '清空' }}
        </el-button>
      </div>

      <div class="toolbar-right">
        <el-select
          v-model="selectedVarName"
          :placeholder="t('message.multiWindow.selectVarPlaceholder')"
          filterable
          clearable
          size="small"
          class="var-select"
          :no-data-text="$t('message.common.noData')"
          :filter-method="handleDropdownFilter"
          @visible-change="handleDropdownVisibleChange"
        >
          <el-option
            v-for="item in filteredVarSetOptions"
            :key="item.value + '_' + item.deviceCode"
            :label="item.label"
            :value="item.value"
          >
            <span class="option-label">{{ item.label }}</span>
            <span class="option-desc">{{ item.deviceCode }} / {{ item.varName }}</span>
          </el-option>
        </el-select>

        <el-button
          type="primary"
          size="small"
          :disabled="!selectedVarOption"
          @click="handleToolbarSetup"
        >
          <el-icon><Setting /></el-icon>
          {{ t('message.multiWindow.setupBtn') }}
        </el-button>

        <el-button type="primary" size="small" @click="handleLoadVariables">
          <el-icon><FolderAdd /></el-icon>
          {{ t('message.multiWindow.loadVarBtn') }}
        </el-button>
      </div>
    </div>

    <!-- ==================== 内容区 ==================== -->
    <div
      ref="contentRef"
      class="multi-window-content"
      :class="layoutMode === 'grid' ? gridClass : 'mdi-area'"
      :style="layoutMode === 'grid' ? gridStyle : {}"
    >
      <!-- ========== 网格模式：比例拖拽条 ========== -->
      <template v-if="layoutMode === 'grid'">
        <div
          v-for="(ratio, colIdx) in visibleColRatios.slice(0, -1)"
          :key="'v-resizer-' + colIdx"
          class="resizer resizer-vertical"
          :style="resizerStyle('v', colIdx)"
          @mousedown="startResize('v', colIdx, $event)"
        >
          <div class="resizer-handle"></div>
        </div>

        <div
          v-for="(ratio, rowIdx) in visibleRowRatios.slice(0, -1)"
          :key="'h-resizer-' + rowIdx"
          class="resizer resizer-horizontal"
          :style="resizerStyle('h', rowIdx)"
          @mousedown="startResize('h', rowIdx, $event)"
        >
          <div class="resizer-handle"></div>
        </div>
      </template>

      <!-- ========== 窗体渲染 ========== -->
      <div
        v-for="win in openedWindows"
        :key="win.key"
        v-show="isActive(win.key)"
        class="panel cell-panel"
        :class="[
          layoutMode === 'grid' ? `cell-${getActiveIndex(win.key)}` : 'mdi-panel',
          {
            'is-maximized': layoutMode === 'mdi' && win.mdi?.maximized,
            /* ★ 网格拖拽：拖动源 / 悬停目标 状态 class */
            'grid-dragging': layoutMode === 'grid' && gridDragSourceKey === win.key,
            'grid-drop-target':
              layoutMode === 'grid' &&
              gridDropTargetIndex === getActiveIndex(win.key) &&
              gridDragSourceKey !== win.key
          }
        ]"
        :style="layoutMode === 'mdi' ? mdiPanelStyle(win) : {}"
        @mousedown.capture="layoutMode === 'mdi' ? bringToFront(win.key) : onGridPanelMouseDown(win, $event)"
      >
        <div
          class="panel-header"
          :class="{
            'mdi-draggable': layoutMode === 'mdi' && !win.mdi?.maximized,
            'grid-draggable': layoutMode === 'grid'
          }"
          @mousedown="onGridPanelHeaderMouseDown(win, $event)"
          @dblclick="layoutMode === 'mdi' && toggleMaximize(win.key)"
        >
          <span class="panel-title">
            <el-icon><component :is="win.icon" /></el-icon>
            {{ win.label }}
          </span>
          <div class="panel-actions" @mousedown.stop>
            <el-button
              v-if="layoutMode === 'mdi'"
              size="small" link
              @click="toggleMaximize(win.key)"
              :title="win.mdi?.maximized ? '还原' : '最大化'"
            >
              <el-icon>
                <CopyDocument v-if="win.mdi?.maximized" />
                <FullScreen v-else />
              </el-icon>
            </el-button>
            <el-button
              v-if="activeWindows.length > 1"
              type="danger" link size="small"
              @click="removeWindow(win.key)"
            >
              <el-icon><Close /></el-icon>
            </el-button>
          </div>
        </div>
        <div class="panel-body">
          <!-- ========== 1. 固定历史数据窗（初始内置） ========== -->
          <template v-if="win.type === 'chart' && win.key === 'history'">
            <ClientProjectHisData
              :varDevices="localVarDevices"
              :projectName="projectName"
              :deviceId="deviceId"
              :showControl="showControl"
              :visible="isActive(win.key)"
            />
          </template>

          <!-- ========== 2. 固定实时数据窗（初始内置） ========== -->
          <template v-else-if="win.type === 'chart' && win.key === 'realtime'">
            <ClientProjectRealTimePlot
              :varDevices="localVarDevices"
              :projectName="projectName"
              :deviceId="deviceId"
              :visible="isActive(win.key)"
            />
          </template>

          <!-- ========== 3. ★ 动态新增：历史曲线子窗 ========== -->
          <template v-else-if="win._dynamic && win.key.startsWith('historyVar')">
            <ClientProjectHisData
              :varDevices="win.varDevices"
              :projectName="win.label"
              :deviceId="deviceId"
              :showControl="showControl"
              :visible="isActive(win.key)"
            />
          </template>

          <!-- ========== 4. ★ 动态新增：实时曲线子窗 ========== -->
          <template v-else-if="win._dynamic && win.key.startsWith('realtimeVar')">
            <ClientProjectRealTimePlot
              :varDevices="win.varDevices"
              :projectName="win.label"
              :deviceId="deviceId"
              :visible="isActive(win.key)"
            />
          </template>

          <!-- ========== 5. 其余 tab 类组件 ========== -->
          <template v-else>
            <component
              :is="getComponentByUrl(win.url)"
              v-if="getComponentByUrl(win.url)"
              :businessId="props.businessId"
              :projectId="props.projectId"
              :collectionId="props.collectionId"
              :domainMenuId="props.domainMenuId"
              :domainId="win.domainId || currentDomainId"
              :showControl="showControl"
              :visible="isActive(win.key)"
            />
            <el-empty v-else :description="'组件未找到: ' + (win.url || 'null')" />
          </template>
        </div>

        <!-- ★ MDI 八方向缩放手柄 -->
        <template v-if="layoutMode === 'mdi' && !win.mdi?.maximized">
          <div class="mdi-resize-handle resize-n" @mousedown.stop="startMdiResize(win.key, 'n', $event)"></div>
          <div class="mdi-resize-handle resize-s" @mousedown.stop="startMdiResize(win.key, 's', $event)"></div>
          <div class="mdi-resize-handle resize-e" @mousedown.stop="startMdiResize(win.key, 'e', $event)"></div>
          <div class="mdi-resize-handle resize-w" @mousedown.stop="startMdiResize(win.key, 'w', $event)"></div>
          <div class="mdi-resize-handle resize-ne" @mousedown.stop="startMdiResize(win.key, 'ne', $event)"></div>
          <div class="mdi-resize-handle resize-nw" @mousedown.stop="startMdiResize(win.key, 'nw', $event)"></div>
          <div class="mdi-resize-handle resize-se" @mousedown.stop="startMdiResize(win.key, 'se', $event)"></div>
          <div class="mdi-resize-handle resize-sw" @mousedown.stop="startMdiResize(win.key, 'sw', $event)"></div>
        </template>
      </div>

      <!-- ★ 网格模式拖动时的幽灵跟随元素 -->
      <div
        v-if="layoutMode === 'grid' && gridDragActive"
        class="grid-drag-ghost"
        :style="{ left: gridGhostX + 'px', top: gridGhostY + 'px' }"
      >
        {{ gridGhostLabel }}
      </div>
    </div>

    <!-- 加载变量弹窗 -->
    <el-dialog
      v-model="loadVarDialogVisible"
      :title="t('message.multiWindow.loadVarDialogTitle')"
      width="900px"
      top="6vh"
      destroy-on-close
      @open="onLoadVarDialogOpen"
    >
      <el-tabs v-model="activeTabId" @tab-change="handleTabChange">
        <el-tab-pane
          v-for="tab in otherTabs"
          :key="tab.id"
          :label="tab.label"
          :name="String(tab.id)"
        />
      </el-tabs>

      <div class="load-var-search-wrap">
        <el-input
          v-model="searchKeyword"
          :placeholder="t('message.common.search')"
          clearable
          prefix-icon="Search"
          size="small"
          style="width: 300px; margin-bottom: 12px;"
        />
      </div>

      <div class="load-var-table-wrap">
        <el-table
          ref="loadVarTableRef"
          v-loading="loadVarTableLoading"
          :data="filteredLoadVarTableData"
          border
          size="small"
          height="420"
          row-key="__rowKey"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="48" reserve-selection />
          <el-table-column prop="title" :label="t('message.multiWindow.colTitle')" min-width="160" show-overflow-tooltip />
          <el-table-column prop="varName" :label="t('message.multiWindow.colVarName')" min-width="140" show-overflow-tooltip />
          <el-table-column prop="deviceCode" :label="t('message.multiWindow.colDeviceCode')" min-width="140" show-overflow-tooltip />
          <el-table-column prop="unit" :label="t('message.multiWindow.colUnit')" width="80" />
          <el-table-column
            v-if="isWritableTab"
            prop="varCmdSwitch"
            :label="t('message.multiWindow.colWritable')"
            width="90"
            align="center"
          >
            <template #default="{ row }">
              <el-tag :type="row.varCmdSwitch === 1 ? 'success' : 'info'" size="small">
                {{ row.varCmdSwitch === 1 ? t('message.multiWindow.yes') : t('message.multiWindow.no') }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <template #footer>
        <div class="load-var-footer">
          <span class="selected-count">
            {{ t('message.multiWindow.selectedCount', { count: selectedRows.length }) }}
          </span>
          <div>
            <el-button @click="loadVarDialogVisible = false">
              {{ t('message.multiWindow.cancel') }}
            </el-button>
            <el-button
              type="primary"
              :disabled="selectedRows.length === 0"
              @click="confirmLoadVariables"
            >
              {{ t('message.multiWindow.confirm') }}
            </el-button>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 设置值弹窗 -->
    <el-dialog
      v-model="SetupValueDialogVisible"
      :title="t('message.multiWindow.dialogTitle', { title: SetupValueForm.title, varName: SetupValueForm.varName })"
      width="400px"
    >
      <el-form :model="SetupValueForm" :label-width="locale === 'en' ? '140px' : '120px'">
        <el-form-item :label="t('message.multiWindow.setValueLabel')">
          <el-input-number
            v-model="SetupValueForm.setValue"
            :min="SetupValueForm.minimum"
            :max="SetupValueForm.maximum"
            :step="SetupValueForm.step"
            :precision="getPrecision(SetupValueForm.step)"
            size="small"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="t('message.multiWindow.description')">
          <span>{{ SetupValueForm.description }}</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="SetupValueDialogVisible = false">
          {{ t('message.multiWindow.cancel') }}
        </el-button>
        <el-button type="primary" @click="confirmSetup">
          {{ t('message.multiWindow.confirm') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>


<script setup>
import { ref, computed, defineProps, defineExpose, nextTick, watch, onMounted, onBeforeUnmount, provide } from 'vue'
import {
  Setting,
  Close,
  FolderAdd,
  Plus,
  Delete,
  Grid,
  CopyDocument,
  FullScreen,
} from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { rollCall } from '@/api/DeviceManagement'
import { getToken } from '@/utils/auth'
import ClientProjectHisData from '@/components/views/ClientProjectHisData.vue'
import ClientProjectRealTimePlot from '@/components/views/ClientProjectRealTimePlot.vue'
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()

import { projectDomainList, projectCollectionsDomainVars } from '@/api/ProjectManger'

import ClientDeviceMonitorSystemMonitorSystem from "@/components/clientViews/ClientDeviceMonitorSystemMonitorSystem1.vue"
import ClientDeviceMonitorSystemMonitorOperation from "@/components/clientViews/ClientDeviceMonitorSystemMonitorOperation.vue"
import ClientDeviceMonitorSystemMonitorAlarm from "@/components/clientViews/ClientDeviceMonitorSystemMonitorAlarm.vue"

const props = defineProps({
  varDevices: { type: Object, required: true, default: () => ({}) },
  projectName: { type: String, default: '' },
  deviceId: { type: [String, Number], default: null },
  projectId: { type: [String, Number], required: true },
  businessId: { type: [String, Number], required: true },
  collectionId: { type: [String, Number], required: true },
  domainMenuId: { type: [String, Number], required: true },
  domainId: { type: [String, Number], default: undefined }
})

// ========== 布局模式 ==========
const layoutMode = ref('mdi')   // 'grid' | 'mdi'，默认给 MDI 更贴合需求

const isInvalidTitle = (title) => {
  if (!title) return true
  return /^&&\d+_/.test(title)
}
const showControl = ref(1)
const localVarDevices = ref(cloneVarDevices(props.varDevices))
watch(() => props.varDevices, (val) => { localVarDevices.value = cloneVarDevices(val) }, { deep: true })
function cloneVarDevices(src) {
  const out = {}
  if (!src) return out
  for (const [code, list] of Object.entries(src)) {
    out[code] = Array.isArray(list) ? list.map(v => ({ ...v })) : []
  }
  return out
}

const isLoading = ref(false)
const otherTabs = ref([])
const currentDomainId = ref(props.domainId)

// ========== 窗体状态 ==========
const initialWindows = [
  { key: 'history', label: t('message.multiWindow.panelHistory') || '历史数据', icon: 'DataLine', type: 'chart' },
  { key: 'realtime', label: t('message.multiWindow.panelRealtime') || '实时数据', icon: 'Monitor', type: 'chart' }
]
const openedWindows = ref([...initialWindows.map(w => ({ ...w }))])
const activeWindows = ref([...initialWindows.map(w => ({ ...w }))])

const tabWindowOptions = ref([])

const hasWindow = (key) => activeWindows.value.some(w => w.key === key)
const isActive = (key) => activeWindows.value.some(w => w.key === key)
const getActiveIndex = (key) => activeWindows.value.findIndex(w => w.key === key)

// ========== 网格布局类 ==========
const gridClass = computed(() => {
  const count = activeWindows.value.length
  if (count === 1) return 'grid-one'
  if (count === 2) return 'grid-two'
  if (count === 3) return 'grid-three'
  if (count === 4) return 'grid-four'
  if (count <= 6) return 'grid-six'
  return 'grid-nine'
})

const availableWindowOptions = computed(() => {
  const list = [
    { key: 'history', label: t('message.multiWindow.panelHistory') || '历史数据', icon: 'DataLine', type: 'chart', disabled: hasWindow('history') },
    { key: 'realtime', label: t('message.multiWindow.panelRealtime') || '实时数据', icon: 'Monitor', type: 'chart', disabled: hasWindow('realtime') },
    { key: 'system', label: t('message.ClientProjectMonitorSystemMonitoring.configuration') || '系统图', icon: 'Grid', url: 'ClientDeviceMonitorSystemMonitorSystem', domainId: 1, type: 'tab', disabled: hasWindow('system') },
    { key: 'operation', label: t('message.ClientProjectMonitorSystemMonitoring.operation') || '运行参数', icon: 'Odometer', url: 'ClientDeviceMonitorSystemMonitorOperation', domainId: 2, type: 'tab', disabled: hasWindow('operation') },
    { key: 'alarm', label: t('message.ClientProjectMonitorSystemMonitoring.alarm') || '报警', icon: 'TrendCharts', url: 'ClientDeviceMonitorSystemMonitorAlarm', domainId: 3, type: 'tab', disabled: hasWindow('alarm') },
    { key: 'setup', label: t('message.ClientProjectMonitorSystemMonitoring.setup') || '系统设置', icon: 'Setting', url: 'ClientDeviceMonitorSystemMonitorOperation', domainId: 5, type: 'tab', disabled: hasWindow('setup') }
  ]
  tabWindowOptions.value.forEach(opt => {
    const isDuplicate = list.some(item => item.url === opt.url && (item.domainId === opt.domainId || opt.domainId <= 5))
    if (!isDuplicate) {
      list.push({
        key: opt.key, label: opt.label, icon: opt.icon || 'Grid',
        url: opt.url, domainId: opt.domainId, type: 'tab', disabled: hasWindow(opt.key)
      })
    }
  })
  return list
})

// ========== MDI 窗体初始位置 ==========
const mdiZCounter = ref(100)

const initMdiPosition = (index) => {
  const baseX = 20 + (index % 5) * 30
  const baseY = 20 + (index % 5) * 30
  return { x: baseX, y: baseY, w: 520, h: 400, z: mdiZCounter.value++, maximized: false }
}

const ensureMdi = (win) => {
  if (!win.mdi) {
    const idx = openedWindows.value.findIndex(w => w.key === win.key)
    win.mdi = initMdiPosition(idx >= 0 ? idx : 0)
  }
  return win.mdi
}

const mdiPanelStyle = (win) => {
  const m = ensureMdi(win)
  if (m.maximized) {
    return { left: '0', top: '0', width: '100%', height: '100%', zIndex: m.z }
  }
  return {
    left: `${m.x}px`,
    top: `${m.y}px`,
    width: `${m.w}px`,
    height: `${m.h}px`,
    zIndex: m.z,
  }
}

// ========== 置顶 ==========
const bringToFront = (key) => {
  const win = openedWindows.value.find(w => w.key === key)
  if (!win) return
  const m = ensureMdi(win)
  if (m.maximized) return
  m.z = ++mdiZCounter.value
}

// ========== 最大化切换 ==========
const toggleMaximize = (key) => {
  const win = openedWindows.value.find(w => w.key === key)
  if (!win) return
  const m = ensureMdi(win)
  if (!m.maximized) {
    m._restore = { x: m.x, y: m.y, w: m.w, h: m.h }
    m.maximized = true
    m.z = ++mdiZCounter.value
  } else {
    m.maximized = false
    if (m._restore) {
      m.x = m._restore.x
      m.y = m._restore.y
      m.w = m._restore.w
      m.h = m._restore.h
    }
  }
}

// ========== MDI 拖动 ==========
let dragging = null
const startDrag = (key, e) => {
  if (layoutMode.value !== 'mdi') return
  const win = openedWindows.value.find(w => w.key === key)
  if (!win || win.mdi?.maximized) return
  e.preventDefault()
  bringToFront(key)
  const m = ensureMdi(win)
  dragging = {
    key,
    startX: e.clientX,
    startY: e.clientY,
    originX: m.x,
    originY: m.y,
  }
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
  document.body.style.cursor = 'move'
  document.body.style.userSelect = 'none'
}
const onDrag = (e) => {
  if (!dragging) return
  const win = openedWindows.value.find(w => w.key === dragging.key)
  if (!win) return
  const m = win.mdi
  const dx = e.clientX - dragging.startX
  const dy = e.clientY - dragging.startY
  const rect = contentRef.value?.getBoundingClientRect()
  const maxX = rect ? rect.width - 100 : 99999
  const maxY = rect ? rect.height - 40 : 99999
  m.x = Math.max(0, Math.min(maxX, dragging.originX + dx))
  m.y = Math.max(0, Math.min(maxY, dragging.originY + dy))
}
const stopDrag = () => {
  dragging = null
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

// ========== MDI 八方向缩放 ==========
let mdiResizing = null

const startMdiResize = (key, dir, e) => {
  const win = openedWindows.value.find(w => w.key === key)
  if (!win || win.mdi?.maximized) return
  e.preventDefault()
  e.stopPropagation()
  bringToFront(key)
  const m = ensureMdi(win)
  mdiResizing = {
    key,
    dir,
    startX: e.clientX,
    startY: e.clientY,
    originX: m.x,
    originY: m.y,
    originW: m.w,
    originH: m.h,
  }
  document.addEventListener('mousemove', onMdiResize)
  document.addEventListener('mouseup', stopMdiResize)
  document.body.style.cursor = getResizeCursor(dir)
  document.body.style.userSelect = 'none'
}

const getResizeCursor = (dir) => {
  const map = {
    n: 'n-resize', s: 's-resize', e: 'e-resize', w: 'w-resize',
    ne: 'ne-resize', nw: 'nw-resize', se: 'se-resize', sw: 'sw-resize',
  }
  return map[dir] || 'default'
}

const MIN_W = 320
const MIN_H = 200

const onMdiResize = (e) => {
  if (!mdiResizing) return
  const win = openedWindows.value.find(w => w.key === mdiResizing.key)
  if (!win) return
  const m = win.mdi
  const { dir, startX, startY, originX, originY, originW, originH } = mdiResizing
  const dx = e.clientX - startX
  const dy = e.clientY - startY

  let newX = originX
  let newY = originY
  let newW = originW
  let newH = originH

  if (dir.includes('e')) {
    newW = Math.max(MIN_W, originW + dx)
  }
  if (dir.includes('w')) {
    const rawW = originW - dx
    newW = Math.max(MIN_W, rawW)
    newX = originX + (originW - newW)
  }

  if (dir.includes('s')) {
    newH = Math.max(MIN_H, originH + dy)
  }
  if (dir.includes('n')) {
    const rawH = originH - dy
    newH = Math.max(MIN_H, rawH)
    newY = originY + (originH - newH)
  }

  const rect = contentRef.value?.getBoundingClientRect()
  if (rect) {
    if (newX < 0) {
      newW += newX
      newX = 0
      newW = Math.max(MIN_W, newW)
    }
    if (newY < 0) {
      newH += newY
      newY = 0
      newH = Math.max(MIN_H, newH)
    }
    if (newX + newW > rect.width) {
      newW = rect.width - newX
    }
    if (newY + newH > rect.height) {
      newH = rect.height - newY
    }
  }

  m.x = newX
  m.y = newY
  m.w = newW
  m.h = newH
}

const stopMdiResize = () => {
  mdiResizing = null
  document.removeEventListener('mousemove', onMdiResize)
  document.removeEventListener('mouseup', stopMdiResize)
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

// ========== 添加 / 移除窗体 ==========
const handleAddWindow = (key) => {
  if (activeWindows.value.length >= 9) {
    ElMessage.warning(t('message.multiWindow.maxWindows') || '最多支持9个窗体')
    return
  }
  if (hasWindow(key)) return
  const opt = availableWindowOptions.value.find(o => o.key === key)
  if (!opt) return

  let target = openedWindows.value.find(w => w.key === key)
  if (!target) {
    target = {
      key: opt.key, label: opt.label, icon: opt.icon,
      url: opt.url, domainId: opt.domainId, type: opt.type
    }
    openedWindows.value.push(target)
  }
  if (layoutMode.value === 'mdi') {
    if (!target.mdi) {
      target.mdi = initMdiPosition(openedWindows.value.length - 1)
    } else {
      target.mdi.z = ++mdiZCounter.value
    }
  }
  activeWindows.value.push(target)
}

const removeWindow = (key) => {
  if (activeWindows.value.length <= 1) {
    ElMessage.warning(t('message.multiWindow.atLeastOnePanel') || '至少保留一个窗体')
    return
  }
  activeWindows.value = activeWindows.value.filter(w => w.key !== key)
  const target = openedWindows.value.find(w => w.key === key)
  if (target?._dynamic) {
    openedWindows.value = openedWindows.value.filter(w => w.key !== key)
  }
}

const clearAllWindows = () => {
  openedWindows.value = openedWindows.value.filter(w => !w._dynamic)
  activeWindows.value = [
    { key: 'history', label: t('message.multiWindow.panelHistory') || '历史数据', icon: 'DataLine', type: 'chart' }
  ]
  if (!openedWindows.value.some(w => w.key === 'history')) {
    openedWindows.value.push({
      key: 'history', label: t('message.multiWindow.panelHistory') || '历史数据', icon: 'DataLine', type: 'chart'
    })
  }
}

// ============================================================
// ★ 动态新增：历史 / 实时曲线子窗体（供 Operation / Alarm 子组件 inject）
// ============================================================
const addVarWindow = (type, varDevices, title) => {
  const isHistory = type === 'history-var'
  const key = `${isHistory ? 'historyVar' : 'realtimeVar'}_${Date.now()}`

  if (activeWindows.value.length >= 9) {
    ElMessage.warning(t('message.multiWindow.maxWindows') || '最多支持9个窗体')
    return
  }

  const win = {
    key,
    label: title || (isHistory
      ? (t('message.multiWindow.panelHistory') || '历史数据')
      : (t('message.multiWindow.panelRealtime') || '实时数据')),
    icon: isHistory ? 'DataLine' : 'Monitor',
    type: 'chart',
    varDevices: JSON.parse(JSON.stringify(varDevices || {})),
    _dynamic: true,
  }

  openedWindows.value.push(win)
  if (layoutMode.value === 'mdi') {
    win.mdi = initMdiPosition(openedWindows.value.length - 1)
  }
  activeWindows.value.push(win)
}

// 供 Operation / Alarm 子组件 inject 使用
provide('multiWindowAddVar', addVarWindow)

// ========== 加载变量 ==========
const loadDefaultParameters = async ({ varNames = null, domainId } = {}) => {
  if (isLoading.value) return []
  try {
    isLoading.value = true
    let nameVarFilters = undefined
    let deviceCodeNamesFilters = undefined
    if (varNames && varNames.length > 0) {
      nameVarFilters = varNames.map(item => item.varName).filter(Boolean)
      deviceCodeNamesFilters = varNames
        .map(item => ({ deviceCode: item.device_code || '', name: item.varName || '' }))
        .filter(item => item.deviceCode && item.name)
    }
    const params = {
      projectId: props.projectId, businessId: props.businessId,
      collectionsId: props.collectionId, domainParentId: props.domainMenuId,
      domainId: domainId ?? props.domainId, companyId: 0,
      groupNames: undefined, names: nameVarFilters, name: undefined,
      display_name: undefined, deviceCodeNames: deviceCodeNamesFilters,
      pageNum: 1, pageSize: 100000
    }
    const res = await projectCollectionsDomainVars(params)
    if (!res.success) { ElMessage.error(res?.msg); return [] }
    if (!res.data?.list || res.data.list.length === 0) return []
    const flat = []
    res.data.list.forEach(group => {
      (group.list_parm || []).forEach(param => {
        if (param.display_name === '未知参数') return
        if (isInvalidTitle(param.display_name)) return
        flat.push({
          varName: param.var_name, title: param.display_name, value: '---',
          unit: param.var_unit?.toString() || '', varSort: param.var_sort ?? 0,
          deviceCode: param.device_code ?? '', updateAt: '',
          defaultValue: param.var_default_value, mult: param.mult ?? 1,
          minimum: param.minimum ?? -200, maximum: param.maximum ?? 999999,
          varDecimal: param.var_decimal, varCmdSwitch: Number(param.var_cmd_switch) || 0,
          description: param.description,
          linkageVars: param.projectInfoBindLinkages || []
        })
      })
    })
    return flat
  } catch (error) {
    console.error('加载变量失败:', error)
    ElMessage.error(error.message || t('message.ClientProjectMonitorSystemMonitorParmater.loadFail'))
    return []
  } finally { isLoading.value = false }
}

const LoadDomainLabel = async () => {
  try {
    const params = {
      all: 0, projectId: Number(props.projectId), businessId: Number(props.businessId),
      collectionId: Number(props.collectionId), companyId: undefined, name: undefined,
      pageNum: 1, pageSize: 1000
    }
    const res = await projectDomainList(params)
    if (!res.success) ElMessage.error(res?.msg)
    if (!res.data?.list || res.data.list.length === 0) {
      otherTabs.value = []; tabWindowOptions.value = [];
    }
    const dynamicTabs = res.data.list
      .filter(item => item.id > 5)
      .map(item => ({ id: item.id, label: item.name, url: item.url }))

    const extraTabs = [
      { id: 2, label: t('message.ClientProjectMonitorSystemMonitoring.operation') || '运行参数', url: 'ClientDeviceMonitorSystemMonitorOperation', icon: 'Odometer', domainId: 2 },
      { id: 3, label: t('message.ClientProjectMonitorSystemMonitoring.alarm') || '报警', url: 'ClientDeviceMonitorSystemMonitorAlarm', icon: 'TrendCharts', domainId: 3 },
      { id: 5, label: t('message.ClientProjectMonitorSystemMonitoring.setup') || '设置', url: 'ClientDeviceMonitorSystemMonitorOperation', icon: 'Setting', domainId: 5 }
    ]
    const merged = [...res.data.list.map(item => ({ id: item.id, label: item.name, url: item.url })), ...extraTabs]
    const seen = new Set()
    otherTabs.value = merged.filter(tab => {
      if (seen.has(tab.id)) return false
      seen.add(tab.id); return true
    }).sort((a, b) => a.id - b.id)

    tabWindowOptions.value = dynamicTabs
      .filter(item => item.url)
      .map(item => ({
        key: `tab_${item.id}`, label: item.label || `Tab ${item.id}`,
        url: item.url, icon: 'Grid', domainId: item.id
      }))
  } catch (error) {
    console.error('加载业务选项失败:', error)
    ElMessage.error(t('message.ClientProjectMonitorSystemMonitoring.loadFailed'))
  }
}

// ========== 组件映射 ==========
const componentMap = {
  'ClientDeviceMonitorSystemMonitorSystem': ClientDeviceMonitorSystemMonitorSystem,
  'ClientDeviceMonitorSystemMonitorOperation': ClientDeviceMonitorSystemMonitorOperation,
  'ClientDeviceMonitorSystemMonitorAlarm': ClientDeviceMonitorSystemMonitorAlarm,
  'system': ClientDeviceMonitorSystemMonitorSystem,
  'operation': ClientDeviceMonitorSystemMonitorOperation,
  'alarm': ClientDeviceMonitorSystemMonitorAlarm,
  'system1': ClientDeviceMonitorSystemMonitorSystem
}
const getComponentByUrl = (url) => {
  if (!url) return null
  const cleanUrl = url.replace(/\.vue$/i, '')
  if (componentMap[cleanUrl]) return componentMap[cleanUrl]
  const lower = cleanUrl.toLowerCase()
  if (lower.includes('operation')) return componentMap['ClientDeviceMonitorSystemMonitorOperation']
  if (lower.includes('alarm')) return componentMap['ClientDeviceMonitorSystemMonitorAlarm']
  if (lower.includes('system')) return componentMap['ClientDeviceMonitorSystemMonitorSystem']
  return null
}

// ========== 加载变量弹窗 ==========
const loadVarDialogVisible = ref(false)
const loadVarTableLoading = ref(false)
const loadVarTableRef = ref(null)
const loadVarTableData = ref([])
const activeTabId = ref('')
const selectedRows = ref([])
const searchKeyword = ref('')

const filteredLoadVarTableData = computed(() => {
  const kw = searchKeyword.value.trim().toLowerCase()
  if (!kw) return loadVarTableData.value
  return loadVarTableData.value.filter(item =>
    (item.title && item.title.toLowerCase().includes(kw)) ||
    (item.varName && item.varName.toLowerCase().includes(kw)) ||
    (item.deviceCode && item.deviceCode.toLowerCase().includes(kw))
  )
})
const isWritableTab = computed(() => loadVarTableData.value.some(item => item.varCmdSwitch === 1))

const onLoadVarDialogOpen = async () => {
  selectedRows.value = []; loadVarTableData.value = []; activeTabId.value = ''; searchKeyword.value = ''
  await LoadDomainLabel()
  if (otherTabs.value.length > 0) {
    activeTabId.value = String(otherTabs.value[0].id)
    await loadTabData(otherTabs.value[0].id)
  }
}
const handleTabChange = async (tabId) => {
  searchKeyword.value = ''
  await loadTabData(Number(tabId))
}
const loadTabData = async (domainId) => {
  loadVarTableLoading.value = true
  try {
    const list = await loadDefaultParameters({ domainId })
    loadVarTableData.value = list.map(item => ({
      ...item, __rowKey: `${domainId}__${item.deviceCode}__${item.varName}`
    }))
    await nextTick()
    if (loadVarTableRef.value) {
      loadVarTableRef.value.clearSelection()
      const keysInThisTab = new Set(loadVarTableData.value.map(r => r.__rowKey))
      const toSelect = selectedRows.value.filter(r => keysInThisTab.has(r.__rowKey))
      toSelect.forEach(row => {
        const target = loadVarTableData.value.find(r => r.__rowKey === row.__rowKey)
        if (target) loadVarTableRef.value.toggleRowSelection(target, true)
      })
    }
  } finally { loadVarTableLoading.value = false }
}
const handleSelectionChange = (rows) => {
  const currentKeys = new Set(filteredLoadVarTableData.value.map(r => r.__rowKey))
  const otherSelections = selectedRows.value.filter(r => !currentKeys.has(r.__rowKey))
  selectedRows.value = [...otherSelections, ...rows]
}
const confirmLoadVariables = () => {
  if (selectedRows.value.length === 0) return
  let originalCount = 0
  for (const list of Object.values(localVarDevices.value)) {
    if (Array.isArray(list)) originalCount += list.length
  }
  let added = 0, skipped = 0
  for (const row of selectedRows.value) {
    if (originalCount + added >= 10) {
      ElMessage.warning(t('message.multiWindow.maxVariablesLimit', { limit: 10 }) || '最多只能添加10个变量')
      break
    }
    const code = row.deviceCode || 'default'
    if (!localVarDevices.value[code]) localVarDevices.value[code] = []
    const list = localVarDevices.value[code]
    const exists = list.some(v => v.name === row.varName)
    if (exists) { skipped++; continue }
    list.push({
      name: row.varName, title: row.title, value: row.value ?? 0,
      unit: row.unit || '', mult: row.mult ?? 1,
      minimum: row.minimum ?? -80, maximum: row.maximum ?? 999999,
      var_decimal: row.varDecimal ?? 0, description: row.description || '',
      varCmdSwitch: row.varCmdSwitch ?? 0, companyId: row.companyId
    })
    added++
  }
  if (added > 0) ElMessage.success(t('message.multiWindow.loadSuccess', { added, skipped }))
  else if (skipped > 0 && added === 0) ElMessage.info(t('message.multiWindow.allVariablesExist') || '所选变量已全部存在')
  loadVarDialogVisible.value = false
}
const handleLoadVariables = () => { loadVarDialogVisible.value = true }

// ========== 可设置变量 ==========
const setupVariables = computed(() => {
  const list = []
  for (const [deviceCode, varObjects] of Object.entries(localVarDevices.value)) {
    varObjects.forEach(varObj => {
      if (varObj.varCmdSwitch === 1) {
        list.push({
          deviceCode, varName: varObj.name, title: varObj.title,
          value: varObj.value ?? 0, unit: varObj.unit || '',
          mult: varObj.mult ?? 1, minimum: varObj.minimum ?? 0,
          maximum: varObj.maximum ?? 999999, varDecimal: varObj.var_decimal ?? 0,
          description: varObj.description || '', varCmdSwitch: varObj.varCmdSwitch,
          companyId: varObj.companyId
        })
      }
    })
  }
  return list
})
const selectedVarName = ref('')
const dropdownKeyword = ref('')
const handleDropdownFilter = (val) => { dropdownKeyword.value = val || '' }
const handleDropdownVisibleChange = (visible) => { if (!visible) dropdownKeyword.value = '' }
const filteredVarSetOptions = computed(() => {
  const kw = dropdownKeyword.value.trim().toLowerCase()
  const options = setupVariables.value.map(item => ({
    label: item.title, value: item.varName, deviceCode: item.deviceCode,
    title: item.title, varName: item.varName, unit: item.unit,
    currentValue: item.value, minimum: item.minimum, maximum: item.maximum,
    varDecimal: item.varDecimal, description: item.description,
    mult: item.mult, companyId: item.companyId
  }))
  if (!kw) return options
  return options.filter(item =>
    item.title.toLowerCase().includes(kw) ||
    item.varName.toLowerCase().includes(kw) ||
    item.deviceCode.toLowerCase().includes(kw)
  )
})
const selectedVarOption = computed(() =>
  filteredVarSetOptions.value.find(item => item.value === selectedVarName.value) || null
)
const handleToolbarSetup = () => {
  const row = selectedVarOption.value
  if (!row) { ElMessage.warning(t('message.multiWindow.pleaseSelectVar')); return }
  handleSetupClick({ ...row, value: row.currentValue })
}

// ========== 设置值 ==========
const SetupValueDialogVisible = ref(false)
const SetupValueForm = ref({
  title: '', value: 0, setValue: 0, unit: '', mult: 1,
  minimum: 0, maximum: 999999, step: 0.1, description: '',
  deviceCode: '', varName: ''
})
const getPrecision = (step) => {
  if (!step) return 2
  const stepStr = step.toString()
  const decimalIndex = stepStr.indexOf('.')
  if (decimalIndex === -1) return 0
  return Math.min(stepStr.length - decimalIndex - 1, 2)
}
const handleSetupClick = (row) => {
  SetupValueForm.value = {
    ...row, setValue: row.value ?? 0,
    step: row.varDecimal ? Math.pow(10, -row.varDecimal) : 0.1
  }
  SetupValueDialogVisible.value = true
}
const confirmSetup = async () => {
  const form = SetupValueForm.value
  const numValue = Number(form.setValue)
  const numMin = Number(form.minimum)
  const numMax = Number(form.maximum)
  if (isNaN(numValue) || isNaN(numMin) || isNaN(numMax)) { ElMessage.error(t('message.multiWindow.invalidNumber')); return }
  if (numValue < numMin || numValue > numMax) {
    ElMessage.error(t('message.multiWindow.valueRange', { min: numMin, max: numMax })); return
  }
  try {
    await ElMessageBox.confirm(
      t('message.multiWindow.confirmSetupMessage', { title: form.title, value: form.setValue, unit: form.unit || '' }),
      t('message.multiWindow.confirmSetupTitle'),
      { type: 'warning', confirmButtonText: t('message.multiWindow.confirm'), cancelButtonText: t('message.multiWindow.cancel') }
    )
    const token = getToken()
    const params = [{
      token: token, deviceCode: form.deviceCode, imei: undefined,
      Dir: 'Down', type: 4, timer: new Date().toISOString(),
      priority: 1, r_data: [{ name: form.varName }],
      w_data: [{ name: form.varName, value: form.setValue / form.mult }]
    }]
    await rollCall(params)
    ElMessage.success(t('message.multiWindow.setupSuccess', { title: form.title }))
    SetupValueDialogVisible.value = false
  } catch (error) {
    if (error !== 'cancel') {
      console.error('设置失败:', error)
      ElMessage.error(t('message.multiWindow.setupFailed', { msg: error.message || t('message.multiWindow.unknownError') }))
    }
  }
}

// ========== 网格比例拖拽 ==========
const contentRef = ref(null)
const colRatios = ref([50, 50])
const rowRatios = ref([100])

const visibleColRatios = computed(() => colRatios.value)
const visibleRowRatios = computed(() => rowRatios.value)

let resizing = false
let resizeType = ''
let resizeIndex = 0
let startPos = 0
let startRatios = []
const MIN_PERCENT = 15

const gridStyle = computed(() => {
  if (activeWindows.value.length <= 1) return {}
  return {
    gridTemplateColumns: colRatios.value.map(r => `${r}%`).join(' '),
    gridTemplateRows: rowRatios.value.map(r => `${r}%`).join(' ')
  }
})

const resetRatios = () => {
  const count = activeWindows.value.length
  if (count === 1) {
    colRatios.value = [100]
    rowRatios.value = [100]
  } else if (count === 2) {
    colRatios.value = [50, 50]
    rowRatios.value = [100]
  } else if (count === 3) {
    colRatios.value = [50, 50]
    rowRatios.value = [50, 50]
  } else if (count === 4) {
    colRatios.value = [50, 50]
    rowRatios.value = [50, 50]
  } else if (count <= 6) {
    colRatios.value = [33.33, 33.33, 33.34]
    rowRatios.value = [50, 50]
  } else {
    colRatios.value = [33.33, 33.33, 33.34]
    rowRatios.value = [33.33, 33.33, 33.34]
  }
}
watch(() => activeWindows.value.length, () => {
  resetRatios()
})
onMounted(() => {
  resetRatios()
})

const resizerStyle = (type, idx) => {
  if (type === 'v') {
    const left = colRatios.value.slice(0, idx + 1).reduce((a, b) => a + b, 0)
    return {
      left: `${left}%`, top: '0', height: '100%', width: '8px',
      transform: 'translateX(-50%)', cursor: 'col-resize',
      position: 'absolute', zIndex: 20
    }
  } else {
    const top = rowRatios.value.slice(0, idx + 1).reduce((a, b) => a + b, 0)
    return {
      top: `${top}%`, left: '0', width: '100%', height: '8px',
      transform: 'translateY(-50%)', cursor: 'row-resize',
      position: 'absolute', zIndex: 20
    }
  }
}

const startResize = (type, idx, e) => {
  e.preventDefault()
  resizing = true
  resizeType = type
  resizeIndex = idx
  startRatios = type === 'v' ? [...colRatios.value] : [...rowRatios.value]
  if (type === 'v') {
    startPos = e.clientX
    document.body.style.cursor = 'col-resize'
  } else {
    startPos = e.clientY
    document.body.style.cursor = 'row-resize'
  }
  document.addEventListener('mousemove', handleResize)
  document.addEventListener('mouseup', stopResize)
}
const handleResize = (e) => {
  if (!resizing || !contentRef.value) return
  const rect = contentRef.value.getBoundingClientRect()
  if (resizeType === 'v') {
    const total = rect.width
    const delta = ((e.clientX - startPos) / total) * 100
    const newLeft = Math.max(MIN_PERCENT, Math.min(100 - MIN_PERCENT, startRatios[resizeIndex] + delta))
    const newRight = Math.max(MIN_PERCENT, Math.min(100 - MIN_PERCENT, startRatios[resizeIndex + 1] - delta))
    const newRatios = [...startRatios]
    newRatios[resizeIndex] = newLeft
    newRatios[resizeIndex + 1] = newRight
    colRatios.value = newRatios
  } else {
    const total = rect.height
    const delta = ((e.clientY - startPos) / total) * 100
    const newTop = Math.max(MIN_PERCENT, Math.min(100 - MIN_PERCENT, startRatios[resizeIndex] + delta))
    const newBottom = Math.max(MIN_PERCENT, Math.min(100 - MIN_PERCENT, startRatios[resizeIndex + 1] - delta))
    const newRatios = [...startRatios]
    newRatios[resizeIndex] = newTop
    newRatios[resizeIndex + 1] = newBottom
    rowRatios.value = newRatios
  }
}
const stopResize = () => {
  resizing = false
  document.body.style.cursor = ''
  document.removeEventListener('mousemove', handleResize)
  document.removeEventListener('mouseup', stopResize)
}

// ============================================================
// ★ 网格模式：拖拽换位（v2 - 修复版）
// ============================================================
const gridDragActive = ref(false)
const gridDragSourceKey = ref(null)
const gridDropTargetIndex = ref(-1)
const gridGhostLabel = ref('')
const gridGhostX = ref(0)
const gridGhostY = ref(0)

let gridDragState = null

// 只判定明确的交互控件，避免误伤标题栏文字
const isInteractiveEl = (el) => {
  if (!el || !el.closest) return false
  return !!el.closest(
    '.el-button, .el-dropdown, .el-select, .el-checkbox, .el-switch, ' +
    '.el-radio, .el-input, .panel-actions, ' +
    'button, input, textarea, select, a'
  )
}

// 标题栏 mousedown 统一入口
const onGridPanelHeaderMouseDown = (win, e) => {
  // 明确阻断，避免与父级 .capture 重复触发
  e.stopPropagation()
  if (layoutMode.value === 'mdi') {
    if (!win.mdi?.maximized) startDrag(win.key, e)
    return
  }
  if (layoutMode.value !== 'grid') return
  if (isInteractiveEl(e.target)) return
  startGridDrag(win.key, e)
}

// 面板主体：MDI 置顶，网格不触发
const onGridPanelMouseDown = (win, e) => {
  if (layoutMode.value === 'mdi') {
    console.log(e.message)
    bringToFront(win.key)
  }
  // 网格模式：仅标题栏可拖，主体区域不触发
}

// 收集格子 DOM：用 className 正则反查 idx，比 querySelector 更稳
const collectCellRects = () => {
  const root = contentRef.value
  if (!root) return []
  const panels = Array.from(root.querySelectorAll('.cell-panel'))
  const result = []
  panels.forEach((el) => {
    const cls = el.className || ''
    const m = cls.match(/\bcell-(\d+)\b/)
    if (!m) return
    const idx = Number(m[1])
    if (!Number.isNaN(idx) && activeWindows.value[idx]) {
      result.push({ idx, el })
    }
  })
  return result
}

const hitTestCell = (x, y) => {
  if (!gridDragState) return -1
  for (const cell of gridDragState.cellRects) {
    const r = cell.el.getBoundingClientRect()
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
      return cell.idx
    }
  }
  return -1
}

const startGridDrag = (key, e) => {
  if (activeWindows.value.length <= 1) return
  const sourceIdx = getActiveIndex(key)
  if (sourceIdx < 0) return
  // 避免与其它拖拽模式并发
  if (dragging || mdiResizing || resizing) return

  // 先不激活视觉，等真正移动超过阈值再激活
  gridDragState = {
    startX: e.clientX,
    startY: e.clientY,
    sourceKey: key,
    sourceIdx,
    cellRects: collectCellRects(),
    moved: false,
  }
  gridDragActive.value = false
  gridDragSourceKey.value = null
  gridDropTargetIndex.value = -1

  document.addEventListener('mousemove', onGridDrag)
  document.addEventListener('mouseup', stopGridDrag)
}

const onGridDrag = (e) => {
  if (!gridDragState) return
  const dx = e.clientX - gridDragState.startX
  const dy = e.clientY - gridDragState.startY
  if (!gridDragState.moved && Math.hypot(dx, dy) < 4) return // 阈值内不算拖动

  if (!gridDragState.moved) {
    // 首次超过阈值：现在才激活拖拽视觉 + 阻止默认 + 锁光标
    gridDragState.moved = true
    gridDragActive.value = true
    gridDragSourceKey.value = gridDragState.sourceKey
    const win = openedWindows.value.find((w) => w.key === gridDragState.sourceKey)
    gridGhostLabel.value = win?.label || ''
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'
  }

  e.preventDefault()
  gridGhostX.value = e.clientX + 8
  gridGhostY.value = e.clientY + 8

  // 每帧刷新格子矩形（防止拖拽期间增删窗体）
  gridDragState.cellRects = collectCellRects()
  const idx = hitTestCell(e.clientX, e.clientY)
  gridDropTargetIndex.value =
    idx >= 0 && idx !== gridDragState.sourceIdx ? idx : -1
}

const stopGridDrag = () => {
  document.removeEventListener('mousemove', onGridDrag)
  document.removeEventListener('mouseup', stopGridDrag)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''

  const state = gridDragState
  const targetIdx = gridDropTargetIndex.value
  gridDragState = null
  gridDragActive.value = false
  gridDragSourceKey.value = null
  gridDropTargetIndex.value = -1
  gridGhostLabel.value = ''

  if (!state || !state.moved) return
  if (targetIdx < 0 || targetIdx === state.sourceIdx) return

  // 交换 activeWindows 中两个元素 → DOM 自动重排到新格子
  const arr = [...activeWindows.value]
  const tmp = arr[state.sourceIdx]
  arr[state.sourceIdx] = arr[targetIdx]
  arr[targetIdx] = tmp
  activeWindows.value = arr
}

// ========== 生命周期清理 ==========
onBeforeUnmount(() => {
  stopResize()
  stopDrag()
  stopMdiResize()
  document.removeEventListener('mousemove', onGridDrag)
  document.removeEventListener('mouseup', stopGridDrag)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
})

// ★ 切换布局时清理网格拖拽状态
watch(layoutMode, () => {
  if (gridDragActive.value || gridDragState) {
    document.removeEventListener('mousemove', onGridDrag)
    document.removeEventListener('mouseup', stopGridDrag)
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
    gridDragState = null
    gridDragActive.value = false
    gridDragSourceKey.value = null
    gridDropTargetIndex.value = -1
    gridGhostLabel.value = ''
  }
})

LoadDomainLabel()
defineExpose({ switchLayout: () => {} })
</script>


<style scoped>
.multi-window-container {
  height: 100%; display: flex; flex-direction: column;
  background: #f5f7fa; overflow: hidden;
}
.multi-window-toolbar {
  flex-shrink: 0; height: 48px; background: #fff;
  border-bottom: 1px solid #e4e7ed;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 16px; gap: 16px;
}
.toolbar-left { display: flex; align-items: center; gap: 8px; overflow-x: auto; flex: 1; }
.toolbar-left :deep(.el-button) { white-space: nowrap; }
.toolbar-right { flex-shrink: 0; display: flex; align-items: center; gap: 8px; }
.var-select { width: 220px; }
.var-select :deep(.el-select__wrapper) { min-height: 28px; }
.option-label { font-size: 13px; color: #303133; }
.option-desc { float: right; margin-left: 12px; font-size: 12px; color: #909399; }

/* ====== 多屏内容区基础 ====== */
.multi-window-content {
  flex: 1; min-height: 0; display: grid; gap: 8px;
  padding: 8px; overflow: hidden;
  position: relative;
}

/* ====== MDI 模式 ====== */
.multi-window-content.mdi-area {
  display: block;
  overflow: hidden;
  background:
    linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px) 0 0 / 20px 20px,
    linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px) 0 0 / 20px 20px,
    #eef2f7;
}

.mdi-panel {
  position: absolute;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.15s;
  min-width: 320px;
  min-height: 200px;
}
.mdi-panel:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}
.mdi-panel.is-maximized {
  border-radius: 0;
  box-shadow: none;
}

/* 标题栏在 MDI 下可拖 */
.panel-header {
  flex-shrink: 0; height: 36px; background: #fafbfc;
  border-bottom: 1px solid #ebeef5;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 12px;
  user-select: none;
}
.panel-header.mdi-draggable {
  cursor: move;
  background: linear-gradient(180deg, #f5f7fa, #ebeef5);
}
.panel-header.mdi-draggable:active {
  background: linear-gradient(180deg, #e8ecf2, #dfe4ea);
}

.panel-title {
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 600; color: #303133;
  min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.panel-body {
  flex: 1; min-height: 0; overflow: hidden; position: relative;
}
.panel-actions {
  display: flex; align-items: center; gap: 2px;
  flex-shrink: 0;
}

/* =====================================================
   ★ MDI 八方向缩放手柄
   ===================================================== */
.mdi-resize-handle {
  position: absolute;
  z-index: 10;
  box-sizing: border-box;
  background: transparent;
}

/* 四边：宽 8px，两端各让出 14px 给四角 */
.resize-n { top: 0;    left: 14px; right: 14px; height: 8px; cursor: n-resize;  }
.resize-s { bottom: 0; left: 14px; right: 14px; height: 8px; cursor: s-resize;  }
.resize-e { right: 0;  top: 14px;  bottom: 14px; width: 8px; cursor: e-resize;  }
.resize-w { left: 0;   top: 14px;  bottom: 14px; width: 8px; cursor: w-resize;  }

/* 四角：14x14 热点 */
.resize-ne { top: 0;    right: 0;  width: 14px; height: 14px; cursor: ne-resize; }
.resize-nw { top: 0;    left: 0;   width: 14px; height: 14px; cursor: nw-resize; }
.resize-se { bottom: 0; right: 0;  width: 14px; height: 14px; cursor: se-resize; }
.resize-sw { bottom: 0; left: 0;   width: 14px; height: 14px; cursor: sw-resize; }

/* 悬停视觉反馈 */
.resize-n:hover, .resize-s:hover, .resize-e:hover, .resize-w:hover,
.resize-ne:hover, .resize-nw:hover, .resize-se:hover, .resize-sw:hover {
  background: rgba(64, 158, 255, 0.15);
}

/* 右下角视觉斜纹提示（仅装饰，不拦截鼠标） */
.resize-se::after {
  content: '';
  position: absolute;
  right: 2px; bottom: 2px;
  width: 10px; height: 10px;
  background:
    linear-gradient(135deg, transparent 45%, #bbb 45%, #bbb 55%, transparent 55%),
    linear-gradient(135deg, transparent 65%, #bbb 65%, #bbb 75%, transparent 75%);
  pointer-events: none;
}

/* =====================================================
   ★ 网格模式：显式定义每个 cell 的 grid 位置
   （关键修复：让 cell-N class 真正决定位置，从而使交换生效）
   ===================================================== */
.grid-one {
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}
.grid-one .cell-0 { grid-column: 1; grid-row: 1; }

.grid-two {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
}
.grid-two .cell-0 { grid-column: 1; grid-row: 1; }
.grid-two .cell-1 { grid-column: 2; grid-row: 1; }

.grid-three {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
}
.grid-three .cell-0 { grid-column: 1; grid-row: 1; }
.grid-three .cell-1 { grid-column: 2; grid-row: 1; }
.grid-three .cell-2 { grid-column: 1 / span 2; grid-row: 2; }

.grid-four {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
}
.grid-four .cell-0 { grid-column: 1; grid-row: 1; }
.grid-four .cell-1 { grid-column: 2; grid-row: 1; }
.grid-four .cell-2 { grid-column: 1; grid-row: 2; }
.grid-four .cell-3 { grid-column: 2; grid-row: 2; }

.grid-six {
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
}
.grid-six .cell-0 { grid-column: 1; grid-row: 1; }
.grid-six .cell-1 { grid-column: 2; grid-row: 1; }
.grid-six .cell-2 { grid-column: 3; grid-row: 1; }
.grid-six .cell-3 { grid-column: 1; grid-row: 2; }
.grid-six .cell-4 { grid-column: 2; grid-row: 2; }
.grid-six .cell-5 { grid-column: 3; grid-row: 2; }

.grid-nine {
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
}
.grid-nine .cell-0 { grid-column: 1; grid-row: 1; }
.grid-nine .cell-1 { grid-column: 2; grid-row: 1; }
.grid-nine .cell-2 { grid-column: 3; grid-row: 1; }
.grid-nine .cell-3 { grid-column: 1; grid-row: 2; }
.grid-nine .cell-4 { grid-column: 2; grid-row: 2; }
.grid-nine .cell-5 { grid-column: 3; grid-row: 2; }
.grid-nine .cell-6 { grid-column: 1; grid-row: 3; }
.grid-nine .cell-7 { grid-column: 2; grid-row: 3; }
.grid-nine .cell-8 { grid-column: 3; grid-row: 3; }

.panel {
  background: #fff; border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  display: flex; flex-direction: column;
  overflow: hidden; min-width: 0; min-height: 0;
}

/* ===== 拖拽条样式（网格模式：调比例） ===== */
.resizer {
  position: absolute;
  display: flex; align-items: center; justify-content: center;
  z-index: 20;
  transition: background 0.2s;
}
.resizer-vertical { top: 0; height: 100%; }
.resizer-horizontal { left: 0; width: 100%; }
.resizer:hover { background: rgba(64, 158, 255, 0.15); }
.resizer-handle {
  width: 2px; height: 40px; background: #dcdfe6;
  border-radius: 2px; transition: background 0.2s, height 0.2s;
}
.resizer-horizontal .resizer-handle { width: 40px; height: 2px; }
.resizer:hover .resizer-handle { background: #409eff; }

/* =====================================================
   ★ 网格模式：拖拽换位
   ===================================================== */

/* 标题栏在网格模式下显示可拖拽光标 */
.panel-header.grid-draggable {
  cursor: grab;
}
.panel-header.grid-draggable:active {
  cursor: grabbing;
}

/* 拖动源：半透明 + 轻微缩小，让"原位"可见 */
.cell-panel.grid-dragging {
  opacity: 0.5;
  transition: opacity 0.15s, transform 0.15s;
  transform: scale(0.98);
}

/* 悬停目标：蓝色虚线边框 + 外发光 */
.cell-panel.grid-drop-target {
  outline: 2px dashed #409eff;
  outline-offset: -2px;
  box-shadow: 0 0 0 4px rgba(64, 158, 255, 0.15);
  transition: box-shadow 0.15s, outline-color 0.15s;
}

/* 幽灵跟随元素（跟随鼠标的窗体标题提示） */
.grid-drag-ghost {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  padding: 6px 12px;
  background: rgba(64, 158, 255, 0.92);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(64, 158, 255, 0.4);
  transform: translate(-50%, -50%);
  white-space: nowrap;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 加载变量弹窗 */
.load-var-search-wrap { padding: 0 4px; }
.load-var-table-wrap { margin-top: 0; }
.load-var-footer { display: flex; align-items: center; justify-content: space-between; }
.selected-count { font-size: 13px; color: #909399; }

/* =====================================================
   ★ 响应式：各断点下重新定义每个 cell 的 grid 位置
   ===================================================== */
@media (max-width: 1200px) {
  /* 4 个：单列 4 行 */
  .grid-four {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(4, 1fr);
  }
  .grid-four .cell-0 { grid-column: 1; grid-row: 1; }
  .grid-four .cell-1 { grid-column: 1; grid-row: 2; }
  .grid-four .cell-2 { grid-column: 1; grid-row: 3; }
  .grid-four .cell-3 { grid-column: 1; grid-row: 4; }

  /* 6 个：2 列 3 行 */
  .grid-six {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
  .grid-six .cell-0 { grid-column: 1; grid-row: 1; }
  .grid-six .cell-1 { grid-column: 2; grid-row: 1; }
  .grid-six .cell-2 { grid-column: 1; grid-row: 2; }
  .grid-six .cell-3 { grid-column: 2; grid-row: 2; }
  .grid-six .cell-4 { grid-column: 1; grid-row: 3; }
  .grid-six .cell-5 { grid-column: 2; grid-row: 3; }

  /* 9 个：2 列 5 行（最后一行跨列） */
  .grid-nine {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(5, 1fr);
  }
  .grid-nine .cell-0 { grid-column: 1; grid-row: 1; }
  .grid-nine .cell-1 { grid-column: 2; grid-row: 1; }
  .grid-nine .cell-2 { grid-column: 1; grid-row: 2; }
  .grid-nine .cell-3 { grid-column: 2; grid-row: 2; }
  .grid-nine .cell-4 { grid-column: 1; grid-row: 3; }
  .grid-nine .cell-5 { grid-column: 2; grid-row: 3; }
  .grid-nine .cell-6 { grid-column: 1; grid-row: 4; }
  .grid-nine .cell-7 { grid-column: 2; grid-row: 4; }
  .grid-nine .cell-8 { grid-column: 1 / span 2; grid-row: 5; }

  /* 3 个：单列 3 行 */
  .grid-three {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(3, 1fr);
  }
  .grid-three .cell-0 { grid-column: 1; grid-row: 1; }
  .grid-three .cell-1 { grid-column: 1; grid-row: 2; }
  .grid-three .cell-2 { grid-column: 1; grid-row: 3; }
}

@media (max-width: 768px) {
  .multi-window-toolbar { height: auto; padding: 8px; flex-wrap: wrap; }
  .toolbar-right { width: 100%; flex-wrap: wrap; }
  .var-select { flex: 1; min-width: 160px; }
  .grid-two, .grid-three, .grid-four, .grid-six, .grid-nine {
    grid-template-columns: 1fr; grid-template-rows: auto; overflow-y: auto;
  }
  /* 小屏全部单列，重置所有 cell 的显式位置为自然流 */
  .grid-two .cell-0, .grid-two .cell-1,
  .grid-three .cell-0, .grid-three .cell-1, .grid-three .cell-2,
  .grid-four .cell-0, .grid-four .cell-1, .grid-four .cell-2, .grid-four .cell-3,
  .grid-six .cell-0, .grid-six .cell-1, .grid-six .cell-2,
  .grid-six .cell-3, .grid-six .cell-4, .grid-six .cell-5,
  .grid-nine .cell-0, .grid-nine .cell-1, .grid-nine .cell-2,
  .grid-nine .cell-3, .grid-nine .cell-4, .grid-nine .cell-5,
  .grid-nine .cell-6, .grid-nine .cell-7, .grid-nine .cell-8 {
    grid-column: 1; grid-row: auto;
  }
  .panel { min-height: 320px; }
  .resizer { display: none; }

  /* 小屏 MDI 自适应：默认最大化 */
  .mdi-panel { min-width: 0; }

  /* ★ 小屏隐藏八方向缩放手柄（避免误触） */
  .mdi-resize-handle { display: none; }

  /* ★ 小屏禁用网格拖拽光标（触摸场景下靠长按，样式上不做提醒） */
  .panel-header.grid-draggable { cursor: default; }
}
</style>